import React from 'react';
import { Box, Typography, Tooltip, Divider } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';
import { REVIEW_TYPES } from '../../../../../constants/reviewTypes';

// Color constants for the ProgressGauge
const GAUGE_COLORS = {
  BACKGROUND_GRAY: '#E5E7EB',
  IN_PROGRESS_LIGHT_BLUE: '#93C5FD',
  COMPLETED_GREEN: '#10B981',
  COMPLETED_BLUE: '#3B82F6',
  TEXT_DARK_GRAY: '#374151',
  TEXT_MEDIUM_GRAY: '#6B7280',
};

const ProgressTooltip = ({
  reviewType,
  completedSteps,
  inProgressSteps,
  totalSteps,
  completedPercentage,
  completedReviewers,
  totalReviewers
}) => {
  const completedColor = completedPercentage === 100 ? GAUGE_COLORS.COMPLETED_GREEN : GAUGE_COLORS.COMPLETED_BLUE;

  if (reviewType === REVIEW_TYPES.SEQUENTIAL) {
    return (
      <Box sx={{ 
        bgcolor: 'white', 
        p: 1.5, 
        borderRadius: 1,
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
        minWidth: '200px'
      }}>
        <Typography variant="body2" component="div" sx={{ 
          fontSize: '0.875rem', 
          mb: 0.5,
          color: completedColor,
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          <Box sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: completedColor
          }} />
          {completedSteps} of {totalSteps} steps completed
        </Typography>
        
        {inProgressSteps > 0 ? (
          <Typography variant="body2" component="div" sx={{ 
            fontSize: '0.875rem', 
            mb: 0.5,
            color: GAUGE_COLORS.IN_PROGRESS_LIGHT_BLUE,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}>
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: GAUGE_COLORS.IN_PROGRESS_LIGHT_BLUE
            }} />
            {inProgressSteps} {inProgressSteps === 1 ? 'step' : 'steps'} in progress
          </Typography>
        ) : (completedPercentage !== 100 && (
          <Typography variant="body2" component="div" sx={{ 
            fontSize: '0.875rem', 
            mb: 0.5,
            color: GAUGE_COLORS.TEXT_MEDIUM_GRAY,
            fontWeight: 400,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}>
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: GAUGE_COLORS.BACKGROUND_GRAY
            }} />
            No steps in progress
          </Typography>
        ))}
          
        <Typography variant="body2" sx={{ 
          fontSize: '0.875rem', 
          mb: 0.5,
          color: GAUGE_COLORS.TEXT_MEDIUM_GRAY,
          fontWeight: 400,
          fontStyle: 'italic',
          ml: 2
        }}>
          → {Math.round(completedPercentage)}% progress based on step completion
        </Typography>

        <Box sx={{ mt: 1.5, mb: 0.5 }}>
          <Typography variant="body2" component="div" sx={{ 
            fontSize: '0.775rem', 
            color: GAUGE_COLORS.TEXT_MEDIUM_GRAY,
            fontWeight: 400,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.5,
            mb: 0.5
          }}>
            <InfoOutlined sx={{ fontSize: 14, color: GAUGE_COLORS.TEXT_MEDIUM_GRAY, mt: 0.1 }} />
            Reviews can contain multiple steps:
          </Typography>
          
          <Box sx={{ ml: 2.5, fontSize: '0.75rem', color: GAUGE_COLORS.TEXT_MEDIUM_GRAY }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.25, fontSize: '0.75rem' }}>
              • Self Assessment: 1 step
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.25, fontSize: '0.75rem' }}>
              • Reviewer's feedback: 2 steps
            </Typography>
            <Box sx={{ ml: 2, fontSize: '0.7rem' }}>
              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', color: GAUGE_COLORS.TEXT_MEDIUM_GRAY, opacity: 0.8 }}>
                → Step 1: Assessment
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', color: GAUGE_COLORS.TEXT_MEDIUM_GRAY, opacity: 0.8 }}>
                → Step 2: Feedback actions
              </Typography>
            </Box>
          </Box>
        </Box>
     
        <Divider sx={{ my: 1 }} />

        <Typography variant="body2" component="div" sx={{ 
          fontSize: '0.875rem',
          color: GAUGE_COLORS.TEXT_DARK_GRAY,
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1
        }}>
          <Box sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: GAUGE_COLORS.TEXT_MEDIUM_GRAY
          }} />
          {completedReviewers} of {totalReviewers} reviews completed
        </Typography>
      </Box>
    );
  }

  // Placeholder for PARALLEL review type
  return (
      <Box sx={{ 
        bgcolor: 'white', 
        p: 1.5, 
        borderRadius: 1,
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
        minWidth: '200px'
      }}>
        <Typography variant="body2" component="div" sx={{ 
          fontSize: '0.875rem', 
          mb: 0.5,
          color: completedColor,
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          <Box sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: completedColor
          }} />
          {completedSteps} of {totalSteps} reviews completed
        </Typography>
        
                 {inProgressSteps > 0 ? (
           <Typography variant="body2" component="div" sx={{ 
             fontSize: '0.875rem', 
             mb: 0.5,
             color: GAUGE_COLORS.IN_PROGRESS_LIGHT_BLUE,
             fontWeight: 500,
             display: 'flex',
             alignItems: 'center',
             gap: 1,
           }}>
             <Box sx={{
               width: 8,
               height: 8,
               borderRadius: '50%',
               backgroundColor: GAUGE_COLORS.IN_PROGRESS_LIGHT_BLUE
             }} />
             {inProgressSteps} {inProgressSteps === 1 ? 'review' : 'reviews'} in progress
           </Typography>
         ) : (completedPercentage !== 100 && (
           <Typography variant="body2" component="div" sx={{ 
             fontSize: '0.875rem', 
             mb: 0.5,
             color: GAUGE_COLORS.TEXT_MEDIUM_GRAY,
             fontWeight: 400,
             display: 'flex',
             alignItems: 'center',
             gap: 1,
           }}>
             <Box sx={{
               width: 8,
               height: 8,
               borderRadius: '50%',
               backgroundColor: GAUGE_COLORS.BACKGROUND_GRAY
             }} />
             No reviews in progress
           </Typography>
         ))}
          
        <Typography variant="body2" sx={{ 
          fontSize: '0.875rem', 
          mb: 0.5,
          color: GAUGE_COLORS.TEXT_MEDIUM_GRAY,
          fontWeight: 400,
          fontStyle: 'italic',
          ml: 2
        }}>
          → {Math.round(completedPercentage)}% progress based on review completion
        </Typography>

      </Box>
  );
};

const ProgressGauge = ({ 
  totalPercentage = 0,
  completedPercentage = 0,
  completedReviewers = 0,
  totalReviewers = 0,
  completedSteps = 0,
  inProgressSteps = 0,
  totalSteps = 1,
  reviewType = REVIEW_TYPES.SEQUENTIAL,
  width = 80,
  height = 80,
}) => {
  const completedColor = completedPercentage === 100 ? GAUGE_COLORS.COMPLETED_GREEN : GAUGE_COLORS.COMPLETED_BLUE;
  
  const tooltipContent = (
    <ProgressTooltip
      reviewType={reviewType}
      completedSteps={completedSteps}
      inProgressSteps={inProgressSteps}
      totalSteps={totalSteps}
      completedPercentage={completedPercentage}
      completedReviewers={completedReviewers}
      totalReviewers={totalReviewers}
    />
  );

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      minWidth: 'fit-content',
      minHeight: height + 50
    }}>
      <Tooltip 
        title={tooltipContent}
        arrow 
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: 'transparent',
              '& .MuiTooltip-arrow': {
                color: 'white'
              }
            }
          }
        }}
      >
        <Box sx={{ 
          position: 'relative', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          width: width,
          height: height,
          mb: 0.5,
          cursor: 'pointer'
        }}>
          {/* Background gauge (full gray) - represents all steps */}
          <Gauge
            value={100}
            startAngle={-90}
            endAngle={90}
            width={width}
            height={height}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              [`& .${gaugeClasses.valueText}`]: {
                display: 'none',
              },
              [`& .${gaugeClasses.valueArc}`]: {
                fill: GAUGE_COLORS.BACKGROUND_GRAY,
              },
              [`& .${gaugeClasses.referenceArc}`]: {
                fill: 'transparent',
              },
            }}
          />
          
          {/* Total progress gauge (light blue) - represents completed + in-progress steps */}
          {totalPercentage > 0 && (
            <Gauge
              value={totalPercentage}
              startAngle={-90}
              endAngle={90}
              width={width}
              height={height}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                [`& .${gaugeClasses.valueText}`]: {
                  display: 'none',
                },
                [`& .${gaugeClasses.valueArc}`]: {
                  fill: GAUGE_COLORS.IN_PROGRESS_LIGHT_BLUE,
                },
                [`& .${gaugeClasses.referenceArc}`]: {
                  fill: 'transparent',
                },
              }}
            />
          )}
          
          {/* Completed gauge (primary blue or green) - represents only completed steps */}
          {completedPercentage > 0 && (
            <Gauge
              value={completedPercentage}
              startAngle={-90}
              endAngle={90}
              width={width}
              height={height}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                [`& .${gaugeClasses.valueText}`]: {
                  display: 'none',
                },
                [`& .${gaugeClasses.valueArc}`]: {
                  fill: completedColor,
                },
                [`& .${gaugeClasses.referenceArc}`]: {
                  fill: 'transparent',
                },
              }}
            />
          )}

          {/* Percentage text centered in the gauge */}
          <Typography 
            variant="caption" 
            sx={{ 
              position: 'absolute',
              top: '57%',
              left: '50%',
              transform: 'translate(-50%, -25%)',
              fontSize: 12,
              fontWeight: 600,
              color: GAUGE_COLORS.TEXT_DARK_GRAY,
              zIndex: 10
            }}
          >
            {Math.round(completedPercentage)}%
          </Typography>
        </Box>
      </Tooltip>
    </Box>
  );
};

export default ProgressGauge; 